import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { getActiveWindow } from 'vscode/vscode/vs/base/browser/dom';
import { Delayer } from 'vscode/vscode/vs/base/common/async';
import { onUnexpectedError } from 'vscode/vscode/vs/base/common/errors';
import { Disposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { MicrotaskDelay } from 'vscode/vscode/vs/base/common/symbols';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { TerminalCapabilityStore } from '../../../../platform/terminal/common/capabilities/terminalCapabilityStore.js';
import { TerminalExtensionsRegistry } from './terminalExtensions.js';
import { TerminalWidgetManager } from './widgets/widgetManager.js';

let DetachedTerminal = class DetachedTerminal extends Disposable {
    get xterm() {
        return this._xterm;
    }
    constructor(_xterm, options, instantiationService) {
        super();
        this._xterm = _xterm;
        this._widgets = this._register(( new TerminalWidgetManager()));
        this.capabilities = ( new TerminalCapabilityStore());
        this._contributions = ( new Map());
        this._register(_xterm);
        const contributionDescs = TerminalExtensionsRegistry.getTerminalContributions();
        for (const desc of contributionDescs) {
            if (( this._contributions.has(desc.id))) {
                onUnexpectedError(( new Error(`Cannot have two terminal contributions with the same id ${desc.id}`)));
                continue;
            }
            if (desc.canRunInDetachedTerminals === false) {
                continue;
            }
            let contribution;
            try {
                contribution = instantiationService.createInstance(desc.ctor, this, options.processInfo, this._widgets);
                this._contributions.set(desc.id, contribution);
                this._register(contribution);
            }
            catch (err) {
                onUnexpectedError(err);
            }
        }
        this._register(( new Delayer(MicrotaskDelay))).trigger(() => {
            for (const contr of ( this._contributions.values())) {
                contr.xtermReady?.(this._xterm);
            }
        });
    }
    get selection() {
        return this._xterm && this.hasSelection() ? this._xterm.raw.getSelection() : undefined;
    }
    hasSelection() {
        return this._xterm.hasSelection();
    }
    clearSelection() {
        this._xterm.clearSelection();
    }
    focus(force) {
        if (force || !getActiveWindow().getSelection()?.toString()) {
            this.xterm.focus();
        }
    }
    attachToElement(container, options) {
        this.domElement = container;
        const screenElement = this._xterm.attachToElement(container, options);
        this._widgets.attachToElement(screenElement);
    }
    forceScrollbarVisibility() {
        this.domElement?.classList.add('force-scrollbar');
    }
    resetScrollbarVisibility() {
        this.domElement?.classList.remove('force-scrollbar');
    }
    getContribution(id) {
        return this._contributions.get(id);
    }
};
DetachedTerminal = ( __decorate([
    ( __param(2, IInstantiationService))
], DetachedTerminal));
class DetachedProcessInfo {
    constructor(initialValues) {
        this.processState = 3 ;
        this.ptyProcessReady = Promise.resolve();
        this.initialCwd = '';
        this.shouldPersist = false;
        this.hasWrittenData = false;
        this.hasChildProcesses = false;
        this.capabilities = ( new TerminalCapabilityStore());
        this.shellIntegrationNonce = '';
        Object.assign(this, initialValues);
    }
}

export { DetachedProcessInfo, DetachedTerminal };
